/**
 * licence-notices - what the console tells a user about the state of their
 * licences, from GET /v1/entitlements/current.
 *
 * The API decides the standing of each module's licence with the same function
 * its route gate uses (api-controller shared/middleware/license-standing.ts),
 * so a notice here is the explanation for a 402 the user will otherwise meet
 * with no warning:
 *
 *   grace        the licence has expired; everything still works until
 *                graceEndsAt, then changes stop          -> warning
 *   restricted   past the grace (or billing suspended): reading and exporting
 *                work, changes are refused (402)          -> danger
 *   refused      billing cancelled: the module is gone    -> danger
 *
 * A module in term says nothing. Pure, so it is tested without a DOM.
 */

export type LicenceStage = 'ok' | 'grace' | 'restricted' | 'refused';

export interface ModuleLicence {
  product: string;
  status: string;
  stage: LicenceStage;
  reason: 'expired' | 'suspended' | 'cancelled' | null;
  expiresAt: string | null;
  graceEndsAt: string | null;
}

export interface LicenceNotice {
  product: string;
  tone: 'warning' | 'danger';
  title: string;
  detail: string;
}

const PRODUCT_LABELS: Record<string, string> = {
  discovery: 'Discovery (DSPM)',
  activity: 'Activity (DAM)',
  ddr: 'Detection (DDR)',
  compliance: 'Compliance',
  foctta_platform: 'Platform',
};

function label(product: string): string {
  return PRODUCT_LABELS[product] ?? product;
}

function day(iso: string | null): string {
  return iso ? iso.slice(0, 10) : 'an unknown date';
}

export function licenceNotices(licences: ReadonlyArray<ModuleLicence> | undefined | null): LicenceNotice[] {
  if (!licences) return [];
  const out: LicenceNotice[] = [];
  for (const l of licences) {
    const name = label(l.product);
    if (l.stage === 'grace') {
      out.push({
        product: l.product,
        tone: 'warning',
        title: `${name}: licence expired ${day(l.expiresAt)}`,
        detail: `Everything keeps working until ${day(l.graceEndsAt)}. After that you can still read and export, but changes stop until the licence is renewed.`,
      });
    } else if (l.stage === 'restricted') {
      out.push({
        product: l.product,
        tone: 'danger',
        title:
          l.reason === 'suspended'
            ? `${name}: subscription suspended`
            : `${name}: licence expired ${day(l.expiresAt)}`,
        detail: 'Read only. Reading and exporting your data keep working; changes are refused until it is renewed. Nothing has been deleted.',
      });
    } else if (l.stage === 'refused') {
      out.push({
        product: l.product,
        tone: 'danger',
        title: `${name}: subscription cancelled`,
        detail: 'This module is no longer licensed. Contact FOCTTA to reinstate it.',
      });
    }
  }
  return out;
}
